export type ScrollAnchor = {
  scroller: HTMLElement;
  name: string;
  occurrence: number;
  offset: number;
};

const SETTLE_MS = 500;
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"];

const findScroller = (el: HTMLElement): HTMLElement => {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
  }
  return (document.scrollingElement ?? document.documentElement) as HTMLElement;
};

const viewportTop = (scroller: HTMLElement) =>
  scroller === document.scrollingElement
    ? 0
    : scroller.getBoundingClientRect().top;

const questionElements = (container: HTMLElement) =>
  Array.from(container.querySelectorAll<HTMLElement>("[data-name]"));

/** The first SurveyJS question at or below the top of the visible area. */
export const captureScrollAnchor = (
  container: HTMLElement | null,
): ScrollAnchor | null => {
  if (!container) return null;
  const scroller = findScroller(container);
  const top = viewportTop(scroller);
  const seen = new Map<string, number>();
  let lastAnchor: ScrollAnchor | null = null;
  for (const el of questionElements(container)) {
    const name = el.dataset.name ?? "";
    const occurrence = seen.get(name) ?? 0;
    seen.set(name, occurrence + 1);
    const offset = el.getBoundingClientRect().top - top;
    const anchor = { scroller, name, occurrence, offset };
    if (offset >= 0) return anchor;
    lastAnchor = anchor;
  }
  return lastAnchor;
};

export const restoreScrollAnchor = (
  container: HTMLElement,
  anchor: ScrollAnchor,
) => {
  const el = questionElements(container).filter(
    (q) => q.dataset.name === anchor.name,
  )[anchor.occurrence];
  if (!el) return;
  anchor.scroller.scrollTop +=
    el.getBoundingClientRect().top -
    viewportTop(anchor.scroller) -
    anchor.offset;
};

/**
 * Keeps the anchor pinned while a freshly rendered form settles (fields and
 * attachments lay out over several frames). Stops early once the user scrolls.
 */
export const holdScrollAnchor = (
  container: HTMLElement,
  anchor: ScrollAnchor,
) => {
  const deadline = performance.now() + SETTLE_MS;
  let frame = 0;
  const stop = () => {
    cancelAnimationFrame(frame);
    USER_SCROLL_EVENTS.forEach((type) =>
      anchor.scroller.removeEventListener(type, stop),
    );
  };
  const tick = () => {
    restoreScrollAnchor(container, anchor);
    if (performance.now() < deadline) {
      frame = requestAnimationFrame(tick);
    } else {
      stop();
    }
  };
  USER_SCROLL_EVENTS.forEach((type) =>
    anchor.scroller.addEventListener(type, stop, { passive: true }),
  );
  tick();
  return stop;
};
